/**
 * Error Translation Utility
 *
 * Translates API error responses into user-friendly localized messages.
 * Uses error codes from the backend to provide consistent translations.
 */

import type { TFunction } from 'i18next';

/**
 * API error response structure from the backend
 */
export interface ApiErrorResponse {
  data: null;
  error: {
    message: string;
    code?: string;
    params?: Record<string, string>;
    details?: Array<{ field?: string; message: string }>;
  };
}

/**
 * Resource types for error messages
 */
export type ErrorResource =
  | 'user'
  | 'users'
  | 'project'
  | 'projects'
  | 'issue'
  | 'issues'
  | 'comment'
  | 'document'
  | 'documents'
  | 'folder'
  | 'testPlan'
  | 'testCase'
  | 'testRun'
  | 'timeEntry'
  | 'checklist'
  | 'runbook'
  | 'phase'
  | 'action'
  | 'template'
  | 'tenant'
  | 'member'
  | 'attachment'
  | 'item'
  | 'file'
  | 'data';

/**
 * Action types for error messages
 */
export type ErrorAction =
  | 'load'
  | 'save'
  | 'create'
  | 'update'
  | 'delete'
  | 'upload'
  | 'download'
  | 'export'
  | 'import'
  | 'fetch'
  | 'submit'
  | 'send'
  | 'assign'
  | 'complete'
  | 'parse';

/**
 * Known error codes from the backend
 */
const KNOWN_ERROR_CODES = [
  'NOT_FOUND',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'BAD_REQUEST',
  'VALIDATION_ERROR',
  'CONFLICT',
  'SERVER_ERROR',
  'SERVICE_UNAVAILABLE',
  'RATE_LIMIT_EXCEEDED',
  'INVALID_TOKEN',
  'INVALID_CREDENTIALS',
  'MISSING_TOKEN',
  'EMAIL_NOT_VERIFIED',
  'EMAIL_ALREADY_REGISTERED',
  'ACCOUNT_DEACTIVATED',
  'TOKEN_EXPIRED',
  'TOKEN_REVOKED',
  'INTERNAL_ERROR',
  'INVALID_STATUS_TRANSITION',
] as const;

type ErrorCode = (typeof KNOWN_ERROR_CODES)[number];

/**
 * Check if an error code is a known error code
 */
function isKnownErrorCode(code: string): code is ErrorCode {
  return KNOWN_ERROR_CODES.includes(code as ErrorCode);
}

/**
 * Extract error code from various error formats
 */
function extractErrorCode(error: unknown): string | undefined {
  if (!error) {
    return undefined;
  }

  // Handle ApiErrorResponse format
  if (typeof error === 'object' && 'error' in error) {
    const apiError = error as ApiErrorResponse;
    return apiError.error?.code;
  }

  // Handle Error with response property (from api.ts)
  if (error instanceof Error && 'response' in error) {
    const response = (error as Error & { response?: ApiErrorResponse }).response;
    return response?.error?.code;
  }

  return undefined;
}

/**
 * Extract error params from various error formats
 */
function extractErrorParams(error: unknown): Record<string, string> | undefined {
  if (!error) {
    return undefined;
  }

  // Handle ApiErrorResponse format
  if (typeof error === 'object' && 'error' in error) {
    const apiError = error as ApiErrorResponse;
    return apiError.error?.params;
  }

  // Handle Error with response property (from api.ts)
  if (error instanceof Error && 'response' in error) {
    const response = (error as Error & { response?: ApiErrorResponse }).response;
    return response?.error?.params;
  }

  return undefined;
}

/**
 * Extract error message from various error formats
 */
function extractErrorMessage(error: unknown): string {
  if (!error) {
    return '';
  }

  // Handle ApiErrorResponse format
  if (typeof error === 'object' && 'error' in error) {
    const apiError = error as ApiErrorResponse;
    return apiError.error?.message ?? '';
  }

  // Handle standard Error
  if (error instanceof Error) {
    // Check for response property
    if ('response' in error) {
      const response = (error as Error & { response?: ApiErrorResponse }).response;
      if (response?.error?.message) {
        return response.error.message;
      }
    }
    return error.message;
  }

  // Handle string
  if (typeof error === 'string') {
    return error;
  }

  return '';
}

/**
 * Translate an API error code to a localized message
 *
 * @param t - Translation function from useTranslation
 * @param code - Error code from the API
 * @param resource - Optional resource name for context (e.g., 'user', 'project')
 * @param params - Optional params from the API for interpolation
 * @returns Translated error message
 */
export function translateErrorCode(
  t: TFunction,
  code: string | undefined,
  resource?: ErrorResource,
  params?: Record<string, string>,
): string {
  if (!code) {
    return t('common:errors.generic');
  }

  // Get translated resource name if provided
  const resourceName = resource
    ? t(`common:errors.resources.${resource}`)
    : t('common:errors.resources.item');

  // Check if we have a translation for this code
  if (isKnownErrorCode(code)) {
    const translated = t(`common:errors.codes.${code}`, { resource: resourceName, ...params });
    // If the translation key doesn't exist, i18next returns the key itself
    if (translated !== `common:errors.codes.${code}`) {
      return translated;
    }
  }

  return t('common:errors.generic');
}

/**
 * Translate an action error (e.g., "Failed to load users")
 *
 * @param t - Translation function from useTranslation
 * @param action - The action that failed (e.g., 'load', 'save')
 * @param resource - The resource type (e.g., 'users', 'project')
 * @returns Translated error message
 */
export function translateActionError(
  t: TFunction,
  action: ErrorAction,
  resource: ErrorResource,
): string {
  const resourceName = t(`common:errors.resources.${resource}`);
  return t(`common:errors.actions.${action}`, { resource: resourceName });
}

/**
 * Get a translated error message from an API error response or Error object
 *
 * This is the main function to use in error handlers. It will:
 * 1. Extract the error code from the error
 * 2. If a code exists and is known, return a translated message for that code
 * 3. Otherwise, return the original error message (which may be English from the API)
 *
 * @param t - Translation function from useTranslation
 * @param error - The error (ApiErrorResponse, Error, or string)
 * @param options - Optional configuration
 * @returns Translated error message
 *
 * @example
 * ```tsx
 * const { t } = useTranslation()
 *
 * try {
 *   await api.delete(`/users/${id}`)
 * } catch (error) {
 *   toast({
 *     title: t('common:status.error'),
 *     description: getTranslatedError(t, error, { resource: 'user' }),
 *     variant: 'destructive'
 *   })
 * }
 * ```
 */
export function getTranslatedError(
  t: TFunction,
  error: unknown,
  options?: {
    /** Resource type for context in error messages */
    resource?: ErrorResource;
    /** Fallback message if no translation is found */
    fallback?: string;
  },
): string {
  const errorCode = extractErrorCode(error);
  const errorParams = extractErrorParams(error);
  const errorMessage = extractErrorMessage(error);

  // If we have a known error code, translate it
  if (errorCode && isKnownErrorCode(errorCode)) {
    return translateErrorCode(t, errorCode, options?.resource, errorParams);
  }

  // If there's an error message, return it (this will be the API message in English)
  // In the future, we could add message pattern matching here to translate common messages
  if (errorMessage) {
    return errorMessage;
  }

  // Use fallback or generic error
  return options?.fallback ?? t('common:errors.generic');
}

/**
 * Create a standardized error handler that translates errors
 *
 * @param t - Translation function from useTranslation
 * @param action - The action being performed
 * @param resource - The resource type
 * @returns Error handler function
 *
 * @example
 * ```tsx
 * const { t } = useTranslation()
 * const handleError = createErrorHandler(t, 'load', 'projects')
 *
 * try {
 *   await api.get('/projects')
 * } catch (error) {
 *   const message = handleError(error)
 *   toast({ description: message, variant: 'destructive' })
 * }
 * ```
 */
export function createErrorHandler(
  t: TFunction,
  action: ErrorAction,
  resource: ErrorResource,
): (error: unknown) => string {
  return (error: unknown) => {
    const errorCode = extractErrorCode(error);

    // If we have a known error code, use it
    if (errorCode && isKnownErrorCode(errorCode)) {
      return translateErrorCode(t, errorCode, resource);
    }

    // Otherwise, return a generic action error
    return translateActionError(t, action, resource);
  };
}
